import * as React from "react";
import { Spinner } from "@fluentui/react-components";
import { useAuth } from "../../context/AuthContext";
import { SectionCard, FieldRow } from "../../components/forms/formPrimitives";
import { REFINED } from "../../components/risikoanalyseStep/tokens";
import type { ProfileSectionProps } from "./profileShell";
import { displayName, userKuerzel } from "./userDisplay";
import {
  fetchMyExtension,
  fetchMyMailboxes,
  type MyExtension,
  type MyMailbox,
} from "./profileApi";

/**
 * Read-only-Darstellung eines Stammdaten-Felds (Label + statischer Wert).
 * Bewusst KEIN `LabeledInput` — das ist auf editierbare Felder (Autofill,
 * Edit-Optik) ausgelegt (Plan §4.4 / Risiko R1). Spiegelt den REFINED-Label-
 * Stil aus `formPrimitives`.
 */
const ReadField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: "grid", rowGap: 6 }}>
    <span
      style={{
        fontFamily: REFINED.fontUI,
        fontWeight: 600,
        fontSize: "0.875rem",
        color: REFINED.ink,
      }}
    >
      {label}
    </span>
    <div
      style={{
        fontFamily: REFINED.fontUI,
        fontSize: "0.95rem",
        color: REFINED.ink2,
        minHeight: "1.2rem",
        wordBreak: "break-word",
      }}
    >
      {children}
    </div>
  </div>
);

/** Übersetzt MailAccess-Codes in eine knappe deutsche Sekundär-Info (P9). */
function mailAccessLabel(access: string): string {
  switch (access) {
    case "read":
      return "Lesen";
    case "write":
    case "send":
      return "Schreiben";
    case "full":
      return "Vollzugriff";
    default:
      return access || "—";
  }
}

interface LoadState {
  extension: MyExtension | null;
  mailboxes: MyMailbox[];
  loading: boolean;
  /** Dezenter Inline-Hinweis, falls Postfächer nicht geladen werden konnten. */
  mailboxError: boolean;
}

export const ProfileStammdaten: React.FC<ProfileSectionProps> = () => {
  const { user, teams } = useAuth();
  const [state, setState] = React.useState<LoadState>({
    extension: null,
    mailboxes: [],
    loading: true,
    mailboxError: false,
  });

  React.useEffect(() => {
    const controller = new AbortController();

    // allSettled (nicht all): ein 404/403 bei der Durchwahl darf die Postfächer
    // nicht killen. Teams kommen aus useAuth() (Konsistenz-Fix P8) → nur 2 Calls.
    Promise.allSettled([
      fetchMyExtension(controller.signal),
      fetchMyMailboxes(controller.signal),
    ]).then(([extRes, mbRes]) => {
      if (controller.signal.aborted) return;
      setState({
        extension: extRes.status === "fulfilled" ? extRes.value : null,
        mailboxes: mbRes.status === "fulfilled" ? mbRes.value : [],
        loading: false,
        mailboxError: mbRes.status === "rejected",
      });
    });

    return () => controller.abort();
  }, []);

  const teamNames = teams.map((t) => t.name).filter(Boolean);

  return (
    <SectionCard title="STAMMDATEN" heading="Stammdaten">
      {state.loading ? (
        <Spinner size="small" label="Stammdaten werden geladen…" />
      ) : (
        <>
          <FieldRow columns={2}>
            <ReadField label="Name">{displayName(user)}</ReadField>
            <ReadField label="Kürzel">{userKuerzel(user)}</ReadField>
            <ReadField label="E-Mail">{user?.email || "—"}</ReadField>
            <ReadField label="Durchwahl / Nebenstelle">
              {state.extension?.extension || "—"}
            </ReadField>
            <ReadField label="Team(s)">
              {teamNames.length ? teamNames.join(", ") : "—"}
            </ReadField>
          </FieldRow>

          <ReadField label="Zugewiesene E-Mails">
            {state.mailboxError ? (
              <span style={{ color: REFINED.err }}>
                Postfächer konnten nicht geladen werden.
              </span>
            ) : state.mailboxes.length === 0 ? (
              "Keine Postfächer zugewiesen"
            ) : (
              <div style={{ display: "flex", flexDirection: "column", rowGap: 6 }}>
                {state.mailboxes.map((mb) => (
                  <div
                    key={mb.id}
                    style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: 8 }}
                  >
                    <span style={{ color: REFINED.ink }}>{mb.mailboxAddress}</span>
                    {mb.displayName && (
                      <span style={{ color: REFINED.ink3, fontSize: "0.85rem" }}>
                        {mb.displayName}
                      </span>
                    )}
                    {/* Sekundär-Info: Zugriffsart pro Postfach (Vorplan §4, Fix P9). */}
                    <span style={{ color: REFINED.ink3, fontSize: "0.8rem" }}>
                      ({mailAccessLabel(mb.mailAccess)})
                    </span>
                  </div>
                ))}
              </div>
            )}
          </ReadField>
        </>
      )}
    </SectionCard>
  );
};

export default ProfileStammdaten;
